import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Steps,
  Space,
  Alert,
  Typography,
  message,
} from 'antd';
import { runAppWizard, type WizardInput } from '@/api/apps';

const { Title, Paragraph, Text } = Typography;

/**
 * 起 App 向导 (P0.4 — TECH_DEBT §4 partial pay-off).
 *
 * 一个表单 → 一次 POST → backend 在一个事务里建好 apps + app_platform_bindings
 * + app_workflow_bindings + workflow_templates.prompts overlay +
 * prompt_templates + content_packages + 2 SKUs + sku_prices.
 *
 * Frontend copy 文件 (src/copy/<slug>.ts + active.ts 加分支) 还要手写 —
 * P1 / §3 抽离后这一步也能省.
 */
export function AppWizardPage() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [form] = Form.useForm<WizardInput & { unlock_price_yuan: number; physical_price_yuan: number }>();
  const [submitting, setSubmitting] = useState(false);
  const [previewValues, setPreviewValues] = useState<Record<string, unknown>>({});

  const onNext = async () => {
    try {
      const values = await form.validateFields(stepFields(current));
      setPreviewValues((v) => ({ ...v, ...values }));
      setCurrent(current + 1);
    } catch {
      // antd 已经把错误标红在字段
    }
  };
  const onPrev = () => setCurrent(Math.max(0, current - 1));

  const onSubmit = async () => {
    const all = { ...previewValues, ...form.getFieldsValue(true) } as WizardInput & {
      unlock_price_yuan: number;
      physical_price_yuan: number;
    };
    setSubmitting(true);
    try {
      const result = await runAppWizard({
        slug: all.slug,
        name: all.name,
        primary_color: all.primary_color,
        workflow_code: all.workflow_code,
        output_count: all.output_count || 1,
        system_prompt: all.system_prompt,
        story_prompt: all.story_prompt,
        unlock_price_cents: Math.round((all.unlock_price_yuan || 9.9) * 100),
        physical_card_price_cents: Math.round((all.physical_price_yuan || 29) * 100),
      });
      message.success(`App #${result.app.id} (${result.app.slug}) 创建成功`);
      navigate(`/apps/${result.app.id}`);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { title: '基础信息', icon: '①' },
    { title: 'Workflow + Prompt', icon: '②' },
    { title: 'SKU 价格', icon: '③' },
    { title: '确认 + 提交', icon: '④' },
  ];

  return (
    <div style={{ maxWidth: 880, margin: '0 auto' }}>
      <Title level={3}>起一个新 App</Title>
      <Paragraph type="secondary">
        填表 → 一次提交 → 后端一个事务建好 apps / workflow 绑定 / prompts / content_package /
        SKU / 价格. <Text type="warning">前端 copy 文件 (src/copy/&lt;slug&gt;.ts) 还要手写,
        P1 (TECH_DEBT §3) 还掉.</Text>
      </Paragraph>

      <Steps current={current} items={steps} style={{ marginBottom: 24 }} />

      <Card>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            primary_color: '#A07248',
            workflow_code: 'WF_CHARACTER_GEN',
            output_count: 4,
            unlock_price_yuan: 9.9,
            physical_price_yuan: 29,
          }}
          preserve
        >
          {/* ───── Step 1: basic ───── */}
          <div style={{ display: current === 0 ? 'block' : 'none' }}>
            <Form.Item
              label="slug (URL / 路由 / SKU code 前缀)"
              name="slug"
              rules={[
                { required: true, message: '必填' },
                { pattern: /^[a-z][a-z0-9-]{1,30}$/, message: '小写字母 / 数字 / 短横线; 字母开头, 2-31 长' },
              ]}
              extra="一旦创建不可改 (FK 引用). 例: greek-oracle / age-progression"
            >
              <Input placeholder="my-new-app" />
            </Form.Item>
            <Form.Item label="App 名 (面向用户)" name="name" rules={[{ required: true }]}>
              <Input placeholder="例: AI 古希腊神谕" />
            </Form.Item>
            <Form.Item label="主色 (hex)" name="primary_color">
              <Input placeholder="#A07248" addonBefore="#" />
            </Form.Item>
          </div>

          {/* ───── Step 2: workflow + prompts ───── */}
          <div style={{ display: current === 1 ? 'block' : 'none' }}>
            <Alert
              type="info"
              showIcon
              message="复用已存在 workflow_template. 新 kind 工作流要先在 /workflows 单独建."
              style={{ marginBottom: 16 }}
            />
            <Form.Item
              label="workflow_code (复用)"
              name="workflow_code"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { value: 'WF_CHARACTER_GEN', label: 'WF_CHARACTER_GEN (灵魂动物 / animal schema)' },
                  { value: 'WF_PAST_LIFE', label: 'WF_PAST_LIFE (persona — 前世/港风/身份类)' },
                ]}
              />
            </Form.Item>
            <Form.Item
              label="套图张数 (output_count)"
              name="output_count"
              extra="1 = 单张人物像 (前世占卜风). 4 = 套系 (港风/灵魂动物风). PersonaGenWorkflow 读它决定生成几张图."
            >
              <InputNumber min={1} max={8} style={{ width: 160 }} />
            </Form.Item>
            <Alert
              type="warning"
              message="prompt 是写到 workflow_templates.prompts JSONB — 跨 App 复用的 workflow_code 会共享这份 prompt. 留空则 worker 回退 binary embed (P0.1 fallback)."
              style={{ marginBottom: 16 }}
            />
            <Form.Item label="system prompt (角色/身份生成)" name="system_prompt">
              <Input.TextArea
                rows={8}
                placeholder="留空 → worker 用 binary embed 默认值"
                style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}
              />
            </Form.Item>
            <Form.Item label="story prompt (200 字短文)" name="story_prompt">
              <Input.TextArea
                rows={8}
                placeholder="留空 → worker 用 binary embed 默认值"
                style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}
              />
            </Form.Item>
          </div>

          {/* ───── Step 3: SKU 价格 ───── */}
          <div style={{ display: current === 2 ? 'block' : 'none' }}>
            <Alert
              type="info"
              message="向导默认建 2 个 SKU: <slug>_unlock (one_time 解锁) + <slug>_physical_card (实体卡 加购). 价格按元填."
              style={{ marginBottom: 16 }}
            />
            <Form.Item label="解锁 SKU 价 (元)" name="unlock_price_yuan" rules={[{ required: true }]}>
              <InputNumber min={0.1} step={0.1} style={{ width: 200 }} addonAfter="元" />
            </Form.Item>
            <Form.Item label="实体卡 SKU 价 (元)" name="physical_price_yuan" rules={[{ required: true }]}>
              <InputNumber min={1} step={1} style={{ width: 200 }} addonAfter="元" />
            </Form.Item>
          </div>

          {/* ───── Step 4: 确认 ───── */}
          <div style={{ display: current === 3 ? 'block' : 'none' }}>
            <Alert
              type="success"
              showIcon
              message="点击 [提交] 后, 后端会在一个事务里建好以下记录:"
              style={{ marginBottom: 16 }}
            />
            <ReviewBlock values={previewValues} formValues={form.getFieldsValue(true)} />
          </div>

          <Space style={{ marginTop: 24 }}>
            {current > 0 && <Button onClick={onPrev}>上一步</Button>}
            {current < steps.length - 1 && (
              <Button type="primary" onClick={onNext}>
                下一步
              </Button>
            )}
            {current === steps.length - 1 && (
              <Button type="primary" loading={submitting} onClick={onSubmit}>
                提交 — 起 App
              </Button>
            )}
            <Button onClick={() => navigate('/apps')}>取消</Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
}

function stepFields(step: number): string[] {
  switch (step) {
    case 0:
      return ['slug', 'name', 'primary_color'];
    case 1:
      return ['workflow_code', 'output_count', 'system_prompt', 'story_prompt'];
    case 2:
      return ['unlock_price_yuan', 'physical_price_yuan'];
    default:
      return [];
  }
}

function ReviewBlock({
  values,
  formValues,
}: {
  values: Record<string, unknown>;
  formValues: Record<string, unknown>;
}) {
  const all = { ...values, ...formValues };
  const slug = String(all.slug || '');
  return (
    <Card type="inner" size="small">
      <pre style={{ fontSize: 12, margin: 0 }}>
{`apps                     slug=${slug}, name=${all.name}, primary_color=${all.primary_color}, status=active
app_platform_bindings    (h5, draft) — 上线时 admin/v1/apps/:id/publish 切 published
app_workflow_bindings    workflow_code=${all.workflow_code}, is_default=true
workflow_templates.prompts (overlay) system=${all.system_prompt ? `${String(all.system_prompt).length} chars` : '(empty → embed)'} | story=${all.story_prompt ? `${String(all.story_prompt).length} chars` : '(empty → embed)'}
prompt_templates         code=${slug}_portrait_v1, version=1 (image-gen 占位 prompt)
content_packages         code=${slug}_default, price_cents=${Math.round((Number(all.unlock_price_yuan) || 9.9) * 100)}
skus                     ${slug}_unlock (one_time, ${all.unlock_price_yuan} 元)
                         ${slug}_physical_card (physical_addon, ${all.physical_price_yuan} 元)
sku_prices               channel=all, 同上价格
`}
      </pre>
      <Alert
        type="warning"
        style={{ marginTop: 12 }}
        message={
          <>
            完成后还要手写 <Text code>frontend/src/copy/{slug}.ts</Text> + 改{' '}
            <Text code>frontend/src/copy/active.ts</Text> 加 case (TECH_DEBT.md §3, P1).
          </>
        }
      />
    </Card>
  );
}
